/**
 * Bildirim görünümü yardımcıları (TI-013): metin, kapsam (tür) sınıfı, aynı kaydın ardışık gelişmelerinin gruplanması ve
 * bildirimden doğru işleme geçiş bağlantısı. Sayılar yalnız yüklenmiş (görünür) bildirimlerden sayılır; sunucunun okunmamış
 * sayacı ayrıca gösterilir.
 */
import type { NotificationItem } from '@castinflow/shared';
import { shellStrings as trf } from '../shell-strings';
import { enabledType, inboxHref, recordHref } from '../routes';

export const NOTIFICATION_CATEGORIES = [
  'all',
  'assign',
  'comment',
  'approval',
  'support',
  'other',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export function notificationCategory(n: NotificationItem): Exclude<NotificationCategory, 'all'> {
  if (n.kind === 'feed_comment') return 'comment';
  if (n.kind === 'approval_requested' || n.topic === 'proposal_link_response') return 'approval';
  if (
    n.topic === 'ticket_assigned' ||
    n.topic === 'ticket_sla_breached' ||
    n.topic === 'ticket_customer_reply'
  )
    return 'support';
  if (
    n.topic === 'record_assigned' ||
    n.topic === 'activity_assigned' ||
    n.topic === 'lead_web_form' ||
    n.topic === null
  )
    return 'assign';
  return 'other';
}

/** Bildirimin cümlesi (mevcut metinler; yeni tür eklenince burada) */
export function notificationText(n: NotificationItem): string {
  const s = trf.notifications;
  switch (n.topic) {
    case 'conversation_message':
      return s.conversationMessage;
    case 'channel_health':
      return s.channelHealth;
    case 'gamification_badge_earned':
      return s.badgeEarned(n.badgeName);
    case 'automation_notice':
      return s.automationNotice(n.ruleName);
    case 'lead_web_form':
      return s.leadWebForm;
    case 'lead_reapplied':
      return s.leadReapplied;
    case 'lead_sequence_step_overdue':
      return s.leadSequenceStepOverdue;
    case 'record_assigned':
      return s.recordAssigned(n.actorName, n.count);
    case 'ticket_assigned':
      return s.ticketAssigned(n.actorName);
    case 'ticket_sla_breached':
      return s.ticketSlaBreached;
    case 'ticket_customer_reply':
      return s.ticketCustomerReply;
    case 'proposal_link_response':
      return s.proposalLinkResponse(n.decision);
    case 'activity_assigned':
      return s.activityAssigned(n.actorName);
    default:
      break;
  }
  if (n.kind === 'approval_requested')
    return n.record?.type === 'form_document'
      ? s.formApprovalRequested(n.actorName)
      : s.approvalRequested(n.actorName);
  if (n.kind === 'feed_comment') return s.feedComment(n.actorName);
  return s.principalAdded(n.actorName, n.fieldLabel);
}

/** Bildirimden asıl işe geçiş: önce işin yeri (onay, gönderi, konuşma), yoksa kayıt. */
export function notificationAction(n: NotificationItem): { href: string; label: string } | null {
  const s = trf.notifications;
  if (n.kind === 'feed_comment' && n.feedItemId !== null)
    return { href: `/feed?oge=${n.feedItemId}`, label: s.openPost };
  if (n.kind === 'approval_requested' && n.record?.type === 'proposal')
    return { href: '/sales/approvals', label: s.openApprovals };
  if (n.topic === 'gamification_badge_earned')
    return { href: '/gamification?tab=badges', label: s.openBadges };
  if (n.record?.type === 'conversation')
    return { href: inboxHref(n.record.id), label: s.openConversation };
  if (n.topic === 'channel_health') return { href: '/settings/channels', label: s.openChannels };
  const t = n.record ? enabledType(n.record.type) : null;
  if (t && n.record) return { href: recordHref(t, n.record.id), label: s.openRecord };
  return null;
}

export interface NotificationGroup {
  key: string;
  head: NotificationItem;
  rest: NotificationItem[];
}

/**
 * Aynı kayda ait aynı türden ARDIŞIK bildirimler tek grupta toplanır (ör. aynı talebin iki SLA uyarısı; aynı gönderiye
 * yorumlar). Sıra korunur; grubun başı en yeni bildirimdir. Kayda/gönderiye bağlı olmayanlar gruplanmaz.
 */
export function groupNotifications(items: readonly NotificationItem[]): NotificationGroup[] {
  const out: NotificationGroup[] = [];
  for (const n of items) {
    // Aynı gönderiye gelen yorumlar da (kayıtsız çalışma alanı notu dahil) tek grup
    const k =
      n.kind === 'feed_comment' && n.feedItemId !== null
        ? `feed_comment:${n.feedItemId}`
        : n.record === null
          ? null
          : `${n.kind}:${n.topic ?? ''}:${n.record.id}`;
    const last = out[out.length - 1];
    if (k !== null && last?.key === k) last.rest.push(n);
    else out.push({ key: k ?? `tek:${n.id}`, head: n, rest: [] });
  }
  return out;
}
